import { Controller } from "stimulus";
import { dateLabel, esc, monthLabel, request } from "../../support/admin-page";

// The Tract Report contributions admin (/admin/tract-report-contributions).
// Every count comes from Admin::Api::V1::TractReport::FacilitiesController; the
// page only filters the list and lays the contributed days out as a calendar.
const API = "/admin/api/v1/tract_report/facilities";
const DAYS = Array.from({ length: 31 }, (_, i) => i + 1);

function plural(count, word) {
    return count + " " + word + (count === 1 ? "" : "s");
}

function daysInMonth(year, month) {
    return new Date(Date.UTC(year, month, 0)).getUTCDate();
}

// Every "YYYY-MM" from the first date's month through the last date's.
function monthsBetween(first, last) {
    const months = [];
    let [year, month] = first.split("-").map(Number);
    const [endYear, endMonth] = last.split("-").map(Number);
    while (year < endYear || (year === endYear && month <= endMonth)) {
        months.push(year + "-" + String(month).padStart(2, "0"));
        month += 1;
        if (month > 12) { month = 1; year += 1; }
    }
    return months;
}

export default class extends Controller {
    static targets = ["summary", "table", "detail", "search", "demo"];

    connect() {
        this.state = { facilities: [], totals: null, selectedKey: null, error: null };
        this.listSeq = 0;
        this.detailSeq = 0;
        this.reload();
    }

    get params() {
        return this.demoTarget.checked ? "?include_demo=1" : "";
    }

    reload() {
        const seq = ++this.listSeq;
        this.summaryTarget.innerHTML = '<p class="tract-report-contributions__muted">Loading contributions…</p>';
        request("GET", API + this.params)
            .then(data => {
                if (seq !== this.listSeq) return;
                Object.assign(this.state, { facilities: data.facilities, totals: data.totals, error: null });
                this.render();
                if (this.state.selectedKey) this.open(this.state.selectedKey);
            })
            .catch(error => {
                if (seq !== this.listSeq) return;
                Object.assign(this.state, { facilities: [], totals: null, error: error.message });
                this.render();
            });
    }

    filter() {
        this.renderTable();
    }

    select(event) {
        this.open(event.currentTarget.dataset.key);
    }

    selectOnEnter(event) {
        if (event.key === "Enter") this.select(event);
    }

    closeDetail() {
        this.state.selectedKey = null;
        this.detailTarget.hidden = true;
        this.renderTable();
    }

    open(key) {
        const seq = ++this.detailSeq;
        this.state.selectedKey = key;
        this.renderTable();
        this.detailTarget.hidden = false;
        this.detailTarget.innerHTML = '<p class="tract-report-contributions__muted tract-report-contributions__pad">Loading…</p>';
        request("GET", API + "/" + encodeURIComponent(key) + this.params)
            .then(detail => {
                if (seq === this.detailSeq) this.detailTarget.innerHTML = this.detailHtml(detail);
            })
            .catch(error => {
                if (seq !== this.detailSeq) return;
                this.detailTarget.innerHTML =
                    '<p class="tract-report-contributions__error tract-report-contributions__pad">' + esc(error.message) + "</p>";
            });
    }

    render() {
        this.renderSummary();
        this.renderTable();
    }

    renderSummary() {
        if (this.state.error) {
            this.summaryTarget.innerHTML = '<p class="tract-report-contributions__error">' + esc(this.state.error) + "</p>";
            return;
        }
        const totals = this.state.totals;
        const tile = (value, label, warn) =>
            '<div class="tract-report-contributions__tile' + (warn ? " tract-report-contributions__tile--warn" : "") + '">' +
            '<span class="tract-report-contributions__tile-value">' + esc(value) + "</span>" +
            '<span class="tract-report-contributions__tile-label">' + esc(label) + "</span></div>";
        const alert = (count, message) => count > 0
            ? '<div class="tract-report-contributions__alert" role="alert">' +
              esc(plural(count, "facility") + " " + (count === 1 ? "has" : "have") + " " + message) + "</div>"
            : "";
        this.summaryTarget.innerHTML =
            '<div class="tract-report-contributions__tiles">' +
            tile(totals.facilities, "Contributing facilities") +
            tile(totals.orgs, "Contributing organizations") +
            tile(totals.multi_claimed, "Claimed by more than one organization", totals.multi_claimed > 0) +
            tile(totals.multi_mapped, "Mapped twice by one organization", totals.multi_mapped > 0) +
            "</div>" +
            alert(totals.multi_claimed,
                "more than one organization. The report counts each organization's copy as a separate facility.") +
            alert(totals.multi_mapped,
                "more than one mapping from the same organization. The report counts each mapping as a separate facility.");
    }

    renderTable() {
        if (this.state.error) {
            this.tableTarget.innerHTML = "";
            return;
        }
        const query = this.searchTarget.value.trim().toLowerCase();
        const rows = this.state.facilities.filter(f =>
            !query || [f.name, f.address, f.key].some(value => String(value || "").toLowerCase().includes(query)));
        if (rows.length === 0) {
            this.tableTarget.innerHTML = '<p class="tract-report-contributions__muted tract-report-contributions__pad">' +
                (this.state.totals ? "No facilities match." : "Loading…") + "</p>";
            return;
        }
        this.tableTarget.innerHTML =
            '<table class="tract-report-contributions__table"><thead><tr>' +
            "<th>Facility</th><th>Address</th><th>Organizations</th><th>Days</th><th>First day</th><th>Last day</th>" +
            "</tr></thead><tbody>" + rows.map(f => this.rowHtml(f)).join("") + "</tbody></table>";
    }

    rowHtml(f) {
        const classes = ["tract-report-contributions__row"];
        if (f.multi_claimed || f.multi_mapped) classes.push("tract-report-contributions__row--warn");
        if (f.key === this.state.selectedKey) classes.push("tract-report-contributions__row--selected");
        const orgs = f.multi_claimed
            ? '<span class="tract-report-contributions__badge tract-report-contributions__badge--warn" title="' +
              esc(f.contributing_org_count + " contributing, " + f.claiming_org_count + " claimed") + '">⚠ ' +
              esc(f.org_count) + "</span>"
            : esc(f.org_count);
        const mappings = f.multi_mapped
            ? ' <span class="tract-report-contributions__badge tract-report-contributions__badge--warn"' +
              ' title="One organization mapped this facility more than once">⚠ ' +
              esc(plural(f.mapping_count, "mapping")) + "</span>"
            : "";
        return '<tr class="' + classes.join(" ") + '" tabindex="0" data-key="' + esc(f.key) + '"' +
            ' data-action="click->admin--tract-report-contributions#select keydown->admin--tract-report-contributions#selectOnEnter">' +
            "<td><strong>" + esc(f.name || "Unnamed facility") + "</strong>" +
            (f.demo ? ' <span class="tract-report-contributions__badge">demo</span>' : "") +
            '<div class="tract-report-contributions__muted tract-report-contributions__small">' +
            esc(f.facility_id ? "ID " + f.facility_id : "Custom address") + "</div></td>" +
            "<td>" + esc(f.address) + "</td>" +
            "<td>" + orgs + mappings + "</td>" +
            "<td>" + esc(f.days_of_data) + "</td>" +
            "<td>" + esc(dateLabel(f.first_date)) + "</td>" +
            "<td>" + esc(dateLabel(f.last_date)) + "</td></tr>";
    }

    detailHtml(detail) {
        const f = detail.facility;
        return '<div class="tract-report-contributions__card-head"><div class="tract-report-contributions__grow">' +
            "<h3>" + esc(f.name || "Unnamed facility") + "</h3>" +
            '<p class="tract-report-contributions__muted">' +
            esc([f.facility_id ? "ID " + f.facility_id : "Custom address", f.address].filter(Boolean).join(" · ")) +
            "</p></div>" +
            '<button type="button" class="tract-report-contributions__close" aria-label="Close"' +
            ' data-action="admin--tract-report-contributions#closeDetail">×</button></div>' +
            '<div class="tract-report-contributions__pad tract-report-contributions__stack">' +
            '<p><strong>' + esc(plural(f.org_count, "organization")) + "</strong> · " +
            esc(plural(f.days_of_data, "day")) + " contributed, " +
            esc(dateLabel(f.first_date)) + " – " + esc(dateLabel(f.last_date)) + "</p>" +
            detail.orgs.map(org => this.orgHtml(org)).join("") + "</div>";
    }

    orgHtml(org) {
        const chips = [];
        if (org.contributing) chips.push('<span class="tract-report-contributions__chip tract-report-contributions__chip--ok">Contributing</span>');
        if (org.claimed) chips.push('<span class="tract-report-contributions__chip">Claimed ' + esc(dateLabel(org.claimed_at.slice(0, 10))) + "</span>");
        if (org.mappings.length > 1) {
            chips.push('<span class="tract-report-contributions__badge tract-report-contributions__badge--warn">⚠ ' +
                esc(org.mappings.length) + " mappings</span>");
        }
        if (org.demo) chips.push('<span class="tract-report-contributions__badge">demo</span>');
        const body = org.mappings.length
            ? org.mappings.map(m => this.mappingHtml(m)).join("")
            : '<p class="tract-report-contributions__muted">Claimed in My Portfolio; no operating data from this organization.</p>';
        return '<div class="tract-report-contributions__org">' +
            '<div class="tract-report-contributions__org-head"><strong>' + esc(org.name || "Organization #" + org.org_id) + "</strong>" +
            '<span class="tract-report-contributions__muted">#' + esc(org.org_id) + "</span>" + chips.join("") + "</div>" +
            body + "</div>";
    }

    mappingHtml(m) {
        const contributed = new Set(m.dates);
        const rows = monthsBetween(m.first_date, m.last_date).map(ym => {
            const [year, month] = ym.split("-").map(Number);
            const length = daysInMonth(year, month);
            let count = 0;
            const cells = DAYS.map(day => {
                if (day > length) return '<span class="tract-report-contributions__day tract-report-contributions__day--none"></span>';
                const iso = ym + "-" + String(day).padStart(2, "0");
                const on = contributed.has(iso);
                if (on) count += 1;
                return '<span class="tract-report-contributions__day' + (on ? " tract-report-contributions__day--on" : "") +
                    '" title="' + esc(dateLabel(iso) + (on ? "" : " (no data)")) + '"></span>';
            }).join("");
            return '<div class="tract-report-contributions__month">' +
                '<span class="tract-report-contributions__month-label">' + esc(monthLabel(ym)) + "</span>" +
                '<span class="tract-report-contributions__days">' + cells + "</span>" +
                '<span class="tract-report-contributions__month-count">' + esc(count + "/" + length) + "</span></div>";
        }).join("");
        return '<div class="tract-report-contributions__mapping">' +
            '<p class="tract-report-contributions__small"><strong>' + esc(m.external_name || m.external_facility_id) + "</strong>" +
            ' <span class="tract-report-contributions__muted">' +
            esc(["their id " + m.external_facility_id, m.match_method, plural(m.days_of_data, "day")].join(" · ")) +
            "</span></p>" +
            '<div class="tract-report-contributions__calendar" aria-label="Days contributed">' + rows + "</div></div>";
    }
}
